import React from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import {
    BUDGET_MOVE_NOTE,
    BUDGET_MOVE_SAFETY_NOTE,
    BUDGET_MOVE_STEP,
    budgetMoveKey,
    describeBudgetMove,
    getBudgetMove,
    hasSelectedAiAudiences,
    isBudgetMoveConfirmed,
} from '@/utils/budget-move'

interface WizardContextType {
    getStepContentData: (stepName: string) => any
    setStepContentData: (data: Record<string, any>, stepName: string) => void
}

interface BudgetMoveConfirmationProps {
    currency?: string
}

/**
 * Asks the user to confirm the budget move of AI audiences added to an existing campaign before they are added.
 * The confirmation covers exactly the shown donor and amount; nothing is moved without it.
 */
const BudgetMoveConfirmation: React.FC<BudgetMoveConfirmationProps> = ({ currency }) => {
    const { getStepContentData, setStepContentData } = useWizard() as WizardContextType
    const salesFunnelData = getStepContentData('salesFunnelSetup')
    const move = hasSelectedAiAudiences(salesFunnelData) ? getBudgetMove(salesFunnelData) : null
    if (!move) return null

    const format = (minorUnits: number) => formatCurrencyAutoLocale({ amount: minorUnits / 100, currency })
    const confirmed = isBudgetMoveConfirmed(move, getStepContentData(BUDGET_MOVE_STEP))

    return (
        <div
            className="tw:mx-6 tw:mt-6 tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:p-4"
            data-testid="budget-move-confirmation"
        >
            <span className="tw:text-sm tw:font-semibold">Budget move</span>
            {describeBudgetMove(move, format).map((line) => (
                <p key={line} className="tw:text-sm">
                    {line}
                </p>
            ))}
            <p className="tw:text-xs tw:text-gray-200">{BUDGET_MOVE_NOTE}</p>
            <p className="tw:text-xs tw:text-gray-200">{BUDGET_MOVE_SAFETY_NOTE}</p>
            <label className="tw:flex tw:items-start tw:gap-2 tw:text-sm tw:cursor-pointer">
                <Checkbox
                    checked={confirmed}
                    onCheckedChange={(checked: boolean) =>
                        setStepContentData({ confirmedFor: checked ? budgetMoveKey(move) : null }, BUDGET_MOVE_STEP)
                    }
                    aria-label="Confirm the budget move"
                    className="tw:mt-0.5"
                />
                <span>
                    {`I confirm: lower the daily budget of "${move.donorName}" from ${format(move.donorBudget)} to ${format(
                        move.donorBudgetAfter,
                    )} once the new audiences are created, and give them ${format(move.movedBudget)} a day.`}
                </span>
            </label>
        </div>
    )
}

export default BudgetMoveConfirmation
